import { makeAutoObservable } from "mobx";
import { PDescription } from "./PDescription";
import { PModifierInstanceId } from "./PModifierInstanceId";
import { PPrice } from "./PPrice";
import { PProductError } from "./PProductError";
import { PTemporarilyUnavailableIngredients } from "./PTemporarilyUnavailableIngredients";
export class PModifierInstanceCreator {
    constructor(params) {
        this.instanceId = new PModifierInstanceId();
        this.domain = params.domain;
        this.modifierInstanceAvailabilityService =
            params.modifierInstanceAvailabilityService;
        this.modifierInstancePriceService = params.modifierInstancePriceService;
        this.modifierType = params.modifierType;
        this.localizationProvider = params.localizationProvider;
        this._sections = this.modifierType.sections.map((modifierItemSection) => params.pModifierSectionCreatorFactory.create({
            modifierItemSection,
            domain: params.domain,
        }));
        this._touch = false;
        this._isEditorVisible = !this.isWrapping;
        makeAutoObservable(this);
    }
    get isVisible() {
        return this.sections.length > 0;
    }
    get isWrapping() {
        return this.domain.behaviour === "Wrapping";
    }
    get isEditable() {
        return this.domain.editability.isPossiblyEditable;
    }
    get isTogglingEditorPossible() {
        return this.isWrapping && this.isEditable;
    }
    get isEditorVisible() {
        return this.isEditable && this._isEditorVisible;
    }
    toggleEditor() {
        this._isEditorVisible = !this._isEditorVisible;
    }
    get typeId() {
        return this.domain.typeId;
    }
    get name() {
        return this.modifierType.name;
    }
    get sections() {
        return this._sections.filter((section) => section.isVisible);
    }
    get isRequired() {
        return !this.modifierType.domain.minItems.isZero;
    }
    touch() {
        this._touch = true;
    }
    get touched() {
        return this._touch || this.sections.some((section) => section.touched);
    }
    get error() {
        if (!this.touched) {
            return null;
        }
        const { minItems, maxItems } = this.modifierType.domain;
        const reasons = this.availability.unavailabilityReasons;
        if (reasons.includes("NotExpectedItems")) {
            return PProductError.notExpectedItems(minItems);
        }
        if (reasons.includes("NotEnoughItems")) {
            return PProductError.notEnoughItems(minItems);
        }
        if (reasons.includes("MaxItemsExceeded") && maxItems !== null) {
            return PProductError.maxItemsExceeded(maxItems);
        }
        return null;
    }
    get price() {
        const p = new PPrice(this.localizationProvider, this.modifierInstancePriceService.price(this.domain.modifierInstance).spectrum).priceValue;
        return p === null ? "" : ` (${p})`;
    }
    get availability() {
        return this.modifierInstanceAvailabilityService.availability(this.domain.modifierInstance);
    }
    get selectedItemsDescription() {
        return PDescription.modifier(this.domain.modifierInstance, this.modifierType);
    }
    get description() {
        return this.modifierType.description;
    }
    get displayMode() {
        return this.modifierType.displayMode;
    }
    get temporarilyUnavailableIngredients() {
        const itemInstanceCreators = this._sections.flatMap((modifierSection) => modifierSection.allItemCreators);
        return PTemporarilyUnavailableIngredients.fromInstanceCreators(this.modifierType, itemInstanceCreators);
    }
}
